import React, { useContext } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Helmet } from 'react-helmet';
import LazyLoad from 'react-lazyload';
import { DiscussionEmbed } from 'disqus-react';
import ConfigContext from '../../../contexts/ConfigContext';
import { useGetBlogPostByIdQuery } from '../../../redux/slices/blogPostSlice';
import { sanitizeHtml } from '../../../modules/formatUtils';
import BlogSidebar from './BlogSidebar';

// Components
import Meta from '../../shared/Meta';

// Misc
import sidebarStyles from '../../../../stylesheets/public/layouts/SidebarLayout.module.postcss';
import styles from './Blog.module.postcss';

function BlogPost() {
  const { id } = useParams();
  const location = useLocation();
  const { data: blogPost } = useGetBlogPostByIdQuery(id);
  const { imageBase, base_url, disqus_shortname } = useContext(ConfigContext);

  if (!blogPost) {
    return null;
  }

  // Canonical post URL (matches the sitemap); blogPost.id is the slug.
  const url = `${base_url}/blog/${blogPost.id}`;
  const photoUrl = `${imageBase}/${blogPost.photo}`;

  const disqusConfig = {
    url,
    identifier: `sft-blog-${blogPost.id}`,
    title: blogPost.title,
  };

  return (
    <div className='Section Section--extraPadding'>
      { blogPost.photo &&
        <Helmet>
          <link rel='preload' as='image' href={photoUrl} fetchpriority='high' />
        </Helmet>
      }
      <Meta
        title={blogPost.title}
        structuredData={{
          blogPost: {
            url,
            headline: blogPost.title,
            image: photoUrl,
            datePublished: dayjs(blogPost.post_date).format(),
            dateModified: dayjs(blogPost.updated_at).format(),
          },
          breadcrumbs: {
            listItems: [
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Blog',
                item: base_url + '/blog',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: blogPost.title,
                item: base_url + location.pathname,
              },
            ],
          },
        }}
      />

      <div className={sidebarStyles.SidebarLayout}>
        <div className={sidebarStyles.SidebarLayout_body}>
          <div>
            <h1>{blogPost.title}</h1>

            <h3>{dayjs(blogPost.post_date).format('MMMM D, YYYY')}</h3>

            <div className={sidebarStyles.BlogPost_image}>
              { blogPost.photo &&
                <img src={photoUrl} alt={blogPost.title} fetchpriority='high' />
              }
            </div>

            <div
              className={styles.BlogPost_body + ' u-mb3'}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(blogPost.body) }}
            />

            <LazyLoad height={500} offset={100} once>
              <DiscussionEmbed
                shortname={disqus_shortname}
                config={disqusConfig}
              />
            </LazyLoad>
          </div>
        </div>
        <div className={sidebarStyles.SidebarLayout_sidebar}>
          <BlogSidebar />
        </div>
      </div>
    </div>
  );
}

export default BlogPost;
